"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import Link from "next/link"

import { NewText, UnitsIcon, transitionTime } from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { transitionEase } from "ui/transitions/utils"
import { getUmbracoMediaUrl, resolveLandingUrl } from "utils"
import { actionButtonClick } from "utils/gtm"
import blurred from "./blurred64"

const StyledCard = styled.div`
  display: flex;
  width: 100%;
  height: 460px;
  position: relative;
  padding: 8px 16px 8px 16px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  border-radius: 16px;
  overflow: hidden;
  background-size: cover;
`

const StyledBottomDiv = styled.div`
  position: absolute;
  bottom: 0;
  height: auto;
  min-height: 97px;
  background: white;
  opacity: 0.8;
  width: 100%;
  padding: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: 103px;
  }
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  position: relative;
  z-index: 2;
`

const StyledLinkWrap = styled.a`
  text-decoration: none;
  img {
    transform: scale(1);
    transition: transform ${transitionTime}s ${transitionEase};
  }
  &:focus-visible,
  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`

const StyledInternalLink = styled(Link)`
  text-decoration: none;
  img {
    transform: scale(1);
    transition: transform ${transitionTime}s ${transitionEase};
  }
  &:focus-visible,
  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`

export type LandingCardData = {
  title: string
  thumbnail: string
  url?: string
  city?: string
  designation?: string
  residentialUnits?: number
}

function LandingCard({
  landing,
  locale,
}: {
  landing: LandingCardData
  locale: string
}) {
  const resolved = resolveLandingUrl(landing, locale)
  if (!resolved) return null

  const cardContent = (
    <StyledCard>
      <Image
        src={getUmbracoMediaUrl(landing.thumbnail || "/")}
        fill
        alt={landing.title || ""}
        style={{ zIndex: 0, objectFit: "cover" }}
        loading="lazy"
        placeholder="blur"
        blurDataURL={blurred}
        sizes="(max-width: 600px) 100vw, 450px"
      />
      <StyledBottomDiv>
        <StyledContent>
          <NewText
            component={"h3"}
            desktopFontSize={16}
            desktopFontWeight={600}
            color={colors.black}
            desktopLineHeight={"23px"}
            mobileFontSize={14}
            mobileLineHeight={"20px"}
          >
            {landing.title}
          </NewText>
          {(landing.city || landing.designation) && (
            <NewText
              component={"span"}
              desktopFontSize={16}
              desktopLineHeight={"24px"}
              mobileFontSize={16}
              mobileLineHeight={"20px"}
              color={colors.black}
            >
              {[landing.designation, landing.city].filter(Boolean).join(" | ")}
            </NewText>
          )}
          {!!landing.residentialUnits && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <UnitsIcon />
              <NewText
                component={"span"}
                desktopFontSize={16}
                desktopLineHeight={"24px"}
                desktopFontWeight={600}
                mobileFontSize={16}
                mobileLineHeight={"20px"}
                color={colors.black}
              >
                {landing.residentialUnits} {locale === "he" ? "יח׳ דיור" : "Housing units"}
              </NewText>
            </div>
          )}
        </StyledContent>
      </StyledBottomDiv>
    </StyledCard>
  )

  if (resolved.external) {
    return (
      <StyledLinkWrap
        href={resolved.href}
        onClick={() => actionButtonClick(landing.title || "", "landing page")}
      >
        {cardContent}
      </StyledLinkWrap>
    )
  }

  return (
    <StyledInternalLink
      href={resolved.href}
      onClick={() => actionButtonClick(landing.title || "", "landing page")}
    >
      {cardContent}
    </StyledInternalLink>
  )
}

export default LandingCard
